import {
  newSpeakerNoteFromAnswer,
  sanitizeSpeakerNoteMarkdown,
  SPEAKER_NOTE_BODY_MAX_CHARS,
  SPEAKER_NOTE_SUMMARY_CHARS_MAX,
  SPEAKER_NOTE_SUMMARY_CHARS_MIN,
  SPEAKER_NOTE_SUMMARY_MAX_WORDS,
  surfBedrockChat,
  truncateSpeakerNoteChars,
  truncateSpeakerNoteMarkdown,
  type SurfApiConfig,
  type SurfSpeakerNote,
  type SurfSpeakerNoteKind,
  type SurfSpeakerNoteSummaryStatus,
} from "@vida/surf-core";

function clampSummaryTargetChars(targetChars?: number): number {
  const value = targetChars ?? SPEAKER_NOTE_BODY_MAX_CHARS;
  return Math.min(
    SPEAKER_NOTE_SUMMARY_CHARS_MAX,
    Math.max(SPEAKER_NOTE_SUMMARY_CHARS_MIN, value),
  );
}

function coherentSummarySystem(targetChars: number): string {
  const approxWords = Math.ceil(targetChars / 5);
  return `Sei l'assistente di un relatore. Data DOMANDA e RISPOSTA dell'agente, produci un sunto in Markdown che RISPONDE alla domanda, coerente e usabile dal vivo.
Regole:
- Massimo ${targetChars} caratteri (~${approxWords} parole).
- 1–3 bullet o un breve paragrafo.
- Mantieni numeri e termini tecnici importanti.
- Niente JSON, preamboli o blocchi codice.
- Lingua = lingua della risposta.`;
}

const SUMMARY_SYSTEM = `Sei un assistente per relatori. Riassumi il testo in Markdown breve e usabile dal vivo durante il discorso.
Regole:
- Massimo ${SPEAKER_NOTE_SUMMARY_MAX_WORDS} parole.
- Output solo Markdown (bullet o brevi paragrafi), in italiano se il testo è in italiano.
- 3–6 punti chiave, niente JSON, niente preamboli.
- Mantieni termini tecnici e numeri importanti.
- Non usare blocchi di codice.`;

export type SpeakerNoteSummaryResult = {
  summary: string;
  status: Extract<SurfSpeakerNoteSummaryStatus, "coherent" | "fallback">;
};

export async function summarizeSpeakerNoteBody(
  cfg: SurfApiConfig,
  body: string,
): Promise<string> {
  const clean = sanitizeSpeakerNoteMarkdown(body);
  if (!clean.trim()) return "";
  try {
    const raw = await surfBedrockChat(cfg, {
      feature: "surf.speaker.note.summary",
      systemText: SUMMARY_SYSTEM,
      userText: `Crea un sunto per il relatore (max ${SPEAKER_NOTE_SUMMARY_MAX_WORDS} parole):\n\n${clean}`,
      maxTokens: 500,
    });
    const summary = sanitizeSpeakerNoteMarkdown(raw);
    if (summary.trim()) {
      return truncateSpeakerNoteMarkdown(summary, SPEAKER_NOTE_SUMMARY_MAX_WORDS);
    }
  } catch {
    /* fallback below */
  }
  return truncateSpeakerNoteMarkdown(clean, SPEAKER_NOTE_SUMMARY_MAX_WORDS);
}

export async function summarizeSpeakerNoteCoherent(
  cfg: SurfApiConfig,
  question: string,
  answer: string,
  opts?: { targetChars?: number },
): Promise<SpeakerNoteSummaryResult> {
  const targetChars = clampSummaryTargetChars(opts?.targetChars);
  const cleanAnswer = sanitizeSpeakerNoteMarkdown(answer);
  const cleanQuestion = question.trim();
  if (!cleanAnswer.trim()) {
    return { summary: "", status: "fallback" };
  }
  try {
    const raw = await surfBedrockChat(cfg, {
      feature: "surf.speaker.note.summary",
      systemText: coherentSummarySystem(targetChars),
      userText: `DOMANDA:\n${cleanQuestion || "(nessuna domanda)"}\n\nRISPOSTA:\n${cleanAnswer}\n\nProduci un sunto che risponde alla domanda (max ${targetChars} caratteri):`,
      maxTokens: Math.ceil(targetChars / 3) + 80,
    });
    const summary = sanitizeSpeakerNoteMarkdown(raw);
    if (summary.trim()) {
      return {
        summary: truncateSpeakerNoteChars(summary, targetChars),
        status: "coherent",
      };
    }
  } catch {
    /* fallback below */
  }
  return {
    summary: truncateSpeakerNoteChars(cleanAnswer, targetChars),
    status: "fallback",
  };
}

export async function buildSpeakerNoteFromWhaleAnswer(
  cfg: SurfApiConfig,
  question: string,
  answer: string,
  agentName?: string,
  jobId?: string,
  kind: SurfSpeakerNoteKind = "freeform",
): Promise<SurfSpeakerNote> {
  const rawBody = sanitizeSpeakerNoteMarkdown(answer);
  const { summary, status } = await summarizeSpeakerNoteCoherent(cfg, question, rawBody);
  return newSpeakerNoteFromAnswer(question, answer, agentName, jobId, kind, {
    body: summary || truncateSpeakerNoteChars(rawBody),
    rawBody,
    question: question.trim() || undefined,
    summaryStatus: status,
    collapsed: true,
  });
}
